import { useMemo, useState } from "react";
import { z } from "zod";
import {
  Box,
  WidgetEmpty,
  WidgetSkeleton,
  ChartFrame,
  ChartText,
  ChartTooltipCard,
  Bar,
  ChartGroup,
  localPoint,
  Treemap,
  hierarchy,
  treemapSquarify,
  treemapBinary,
  treemapDice,
  treemapSlice,
  treemapSliceDice,
  treemapResquarify,
  useCategoricalColors,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { WIDGET_DIMENSIONS, WIDGET_DIMENSION_OPTIONS } from "#app/widgets/dashboards/use-dimension-counts";
import { useDimensionSlices } from "#app/widgets/dashboards/use-dimension-slices";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { portfolioTitle } from "../shared/derived-titles";

/** A hovered tile, with the svg-relative pointer position for the tooltip. */
type Hover = { x: number; y: number; title: string; detail: string };

/**
 * `portfolio-map` — a treemap of projects tiled by any grouping dimension (the
 * hierarchy family). Pressure-tests the "layout then themed marks" pattern (a
 * `Treemap` root drawn with `Bar`/`ChartText`) AND a settings schema that reaches
 * into visx internals: the `dimension` and tiling `select`s, an inner-padding
 * `number`, and a top-N cap. Every tile is a themed `Bar` mark wearing its
 * entity's OWN color (`useCategoricalColors`, keyed by tile identity — layout
 * order never repaints a category).
 */
export const portfolioMapConfigSchema = z
  .looseObject({
    dimension: z.enum(WIDGET_DIMENSIONS).default("type"),
    tiling: z
      .enum(["squarify", "binary", "dice", "slice", "sliceDice", "resquarify"])
      .default("squarify"),
    padding: z.int().min(0).max(8).default(3),
    maxTiles: z.int().min(2).max(16).default(10),
    showValues: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  });

export type PortfolioMapConfig = z.infer<typeof portfolioMapConfigSchema>;

/** Leaf datum the treemap sums over (`id` carries the drilldown category id). */
type TreeDatum = { name: string; total: number; id?: string; children?: TreeDatum[] };

const TILE = {
  squarify: treemapSquarify,
  binary: treemapBinary,
  dice: treemapDice,
  slice: treemapSlice,
  sliceDice: treemapSliceDice,
  resquarify: treemapResquarify,
};

function PortfolioMapRender({ config }: WidgetRenderContext<PortfolioMapConfig>) {
  const { data, loading, error } = useDimensionSlices(config.dimension, config.filters);
  const [hover, setHover] = useState<Hover | null>(null);
  // Click-through: a tile press opens this card's Data drawer on the category's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const press =
    drill && instance
      ? (id: string, name: string) => {
          setHover(null);
          drill.openData(instance.instanceId, { kind: "dimension", dimension: config.dimension, ids: [id], label: name });
        }
      : undefined;

  const leaves = useMemo(() => data.slice(0, Math.max(2, config.maxTiles)), [data, config.maxTiles]);
  const sliceColors = useCategoricalColors(leaves);
  // The treemap re-sorts nodes for layout — key the color by tile identity,
  // never by render index.
  const tokenByLabel = useMemo(
    () => new Map(leaves.map((d, i) => [d.label, sliceColors.tokens[i]])),
    [leaves, sliceColors],
  );

  const root = useMemo(
    () =>
      hierarchy<TreeDatum>({
        name: "root",
        total: 0,
        children: leaves.map((d) => ({ name: d.label, total: d.total, id: d.id })),
      })
        .sum((d) => d.total)
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0)),
    [leaves],
  );

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="block" />;
  }
  if (leaves.length === 0) {
    return <WidgetEmpty icon="layout-grid" title="No projects in range" />;
  }

  return (
    <Box fill="flex" position="relative">
      <ChartFrame margin={{ top: 0, right: 0, bottom: 0, left: 0 }} ariaLabel="Project portfolio map">
      {({ innerWidth, innerHeight }) => {
        if (innerWidth <= 0 || innerHeight <= 0) return null;
        return (
          <Treemap<TreeDatum>
            root={root}
            size={[innerWidth, innerHeight]}
            tile={TILE[config.tiling]}
            round
            paddingInner={config.padding}
          >
            {(tile) => (
              <ChartGroup>
                {tile.leaves().map((leaf, i) => {
                  const nodeW = leaf.x1 - leaf.x0;
                  const nodeH = leaf.y1 - leaf.y0;
                  const roomy = nodeW > 56 && nodeH > 32;
                  const id = leaf.data.id;
                  return (
                    <ChartGroup
                      key={`${leaf.data.name}-${i}`}
                      left={leaf.x0}
                      top={leaf.y0}
                      cursor={press && id ? "pointer" : undefined}
                      onMouseMove={(e) => {
                        const p = localPoint(e);
                        if (p)
                          setHover({
                            x: p.x,
                            y: p.y,
                            title: leaf.data.name,
                            detail: `${leaf.data.total} ${leaf.data.total === 1 ? "project" : "projects"}`,
                          });
                      }}
                      onMouseLeave={() => setHover(null)}
                      onClick={press && id ? () => press(id, leaf.data.name) : undefined}
                    >
                      <Bar x={0} y={0} width={nodeW} height={nodeH} rx={3} colorToken={tokenByLabel.get(leaf.data.name)} />
                      {roomy ? (
                        <>
                          <ChartText
                            x={7}
                            y={16}
                            width={nodeW - 14}
                            fontSize={12}
                            fontWeight={600}
                            on={sliceColors.token(leaf.data.name)}
                          >
                            {leaf.data.name}
                          </ChartText>
                          {config.showValues ? (
                            <ChartText x={7} y={31} fontSize={11} on={sliceColors.token(leaf.data.name)}>
                              {String(leaf.data.total)}
                            </ChartText>
                          ) : null}
                        </>
                      ) : null}
                    </ChartGroup>
                  );
                })}
              </ChartGroup>
            )}
          </Treemap>
        );
      }}
      </ChartFrame>
      {hover ? (
        <ChartTooltipCard left={hover.x} top={hover.y}>
          <WidgetChartTooltip label={hover.title} details={hover.detail} />
        </ChartTooltipCard>
      ) : null}
    </Box>
  );
}

export const portfolioMapDef: WidgetModule<PortfolioMapConfig> = {
  type: "portfolio-map",
  title: "Portfolio map",
  dynamicTitle: (config) => portfolioTitle(config.title, config.dimension),
  icon: "layout-grid",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 10, defaultW: 5, defaultH: 4 },
  configSchema: portfolioMapConfigSchema,
  headerActions: WidgetHeaderFilters,
  settings: [
    {
      title: "Data",
      fields: [
        {
          key: "dimension",
          kind: "select",
          label: "Group by",
          icon: "layers",
          options: WIDGET_DIMENSION_OPTIONS,
        },
        { key: "maxTiles", kind: "number", label: "Max tiles", min: 2, max: 16, step: 1 },
      ],
    },
    {
      title: "Layout",
      fields: [
        {
          key: "tiling",
          kind: "select",
          label: "Tiling",
          options: [
            { value: "squarify", label: "Squarify" },
            { value: "binary", label: "Binary" },
            { value: "dice", label: "Dice" },
            { value: "slice", label: "Slice" },
            { value: "sliceDice", label: "Slice + dice" },
            { value: "resquarify", label: "Resquarify" },
          ],
        },
        { key: "padding", kind: "number", label: "Tile gap", min: 0, max: 8, step: 1 },
      ],
    },
    {
      title: "Labels",
      fields: [{ key: "showValues", kind: "toggle", label: "Show counts" }],
    },
    {
      title: "Filters",
      fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
    },
  ],
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "portfolio-map.workflow-map",
      label: "Workflow map",
      description: "A treemap of workflow work by type, stage, team, assignee, or health.",
      section: "Workflow Dashboard",
      tags: ["projects", "types", "stages", "teams", "members", "ageHealth"] satisfies readonly WidgetTag[],
      defaultConfig: { dimension: "stage", tiling: "squarify", maxTiles: 10, showValues: true },
      defaultW: 5,
      defaultH: 4,
    },
    {
      key: "portfolio-map.treemap",
      label: "Treemap",
      description: "Compare grouped totals as proportional tiles.",
      section: "Custom",
      defaultConfig: { dimension: "type", tiling: "squarify", maxTiles: 10, showValues: true },
      defaultW: 5,
      defaultH: 4,
    },
  ],
  render: PortfolioMapRender,
};
